import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormGroup,
  Checkbox,
  TextField,
  Typography,
  Chip,
  Paper,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { RecipientFilter } from '../../../types/notification-admin';
import { UserRole } from '../../../types/auth';
import { NotificationBroadcastService } from '../../../services/notificationBroadcastService';

interface UserSelectorProps {
  value: RecipientFilter;
  onChange: (filter: RecipientFilter) => void;
  disabled?: boolean;
}

/**
 * UserSelector - Component for selecting broadcast recipients
 * Supports multiple filter options:
 * - All users
 * - By role
 * - By activity status
 * - By registration date
 * - Custom user list
 */
const UserSelector: React.FC<UserSelectorProps> = ({ value, onChange, disabled = false }) => {
  const [selectionMode, setSelectionMode] = useState<'all' | 'filtered' | 'custom'>('all');
  const [recipientCount, setRecipientCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Initialize selection mode based on value
  useEffect(() => {
    if (value.customUserIds && value.customUserIds.length > 0) {
      setSelectionMode('custom');
    } else if (value.allUsers) {
      setSelectionMode('all');
    } else {
      setSelectionMode('filtered');
    }
  }, []);

  // Update recipient count when filter changes
  useEffect(() => {
    const updateCount = async () => {
      if (disabled) return;

      setLoading(true);
      try {
        const count = await NotificationBroadcastService.getRecipientCount(value);
        setRecipientCount(count);
      } catch (error) {
        console.error('Error getting recipient count:', error);
        setRecipientCount(0);
      } finally {
        setLoading(false);
      }
    };

    updateCount();
  }, [value, disabled]);

  const handleModeChange = (mode: 'all' | 'filtered' | 'custom') => {
    setSelectionMode(mode);

    if (mode === 'all') {
      onChange({ allUsers: true });
    } else if (mode === 'filtered') {
      onChange({
        roles: [],
        activityStatus: undefined,
        registrationDateRange: undefined,
      });
    } else {
      onChange({ customUserIds: [] });
    }
  };

  const handleRoleChange = (role: UserRole, checked: boolean) => {
    const currentRoles = value.roles || [];
    const newRoles = checked
      ? [...currentRoles, role]
      : currentRoles.filter(r => r !== role);

    onChange({
      ...value,
      roles: newRoles.length > 0 ? newRoles : undefined,
    });
  };

  const handleActivityStatusChange = (status: 'active' | 'inactive' | undefined) => {
    onChange({
      ...value,
      activityStatus: status,
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', date: Date | null) => {
    if (!date) {
      if (field === 'start' && !value.registrationDateRange?.end) {
        onChange({ ...value, registrationDateRange: undefined });
      } else if (field === 'end' && !value.registrationDateRange?.start) {
        onChange({ ...value, registrationDateRange: undefined });
      } else {
        onChange({
          ...value,
          registrationDateRange: {
            start: field === 'start' ? new Date() : value.registrationDateRange?.start || new Date(),
            end: field === 'end' ? new Date() : value.registrationDateRange?.end || new Date(),
          },
        });
      }
      return;
    }

    onChange({
      ...value,
      registrationDateRange: {
        start: field === 'start' ? date : value.registrationDateRange?.start || new Date(),
        end: field === 'end' ? date : value.registrationDateRange?.end || new Date(),
      },
    });
  };

  const handleCustomUserIdsChange = (text: string) => {
    // Parse comma-separated or newline-separated user IDs
    const userIds = text
      .split(/[,\n]/)
      .map(id => id.trim())
      .filter(id => id.length > 0);

    onChange({
      ...value,
      customUserIds: userIds,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Recipients
      </Typography>

      <FormControl component="fieldset" fullWidth disabled={disabled}>
        <RadioGroup
          value={selectionMode}
          onChange={(e) => handleModeChange(e.target.value as any)}
        >
          <FormControlLabel
            value="all"
            control={<Radio />}
            label="All Users"
          />
          <FormControlLabel
            value="filtered"
            control={<Radio />}
            label="Filter by Criteria"
          />
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label="Custom User List"
          />
        </RadioGroup>
      </FormControl>

      {/* Filtered Mode Options */}
      {selectionMode === 'filtered' && (
        <Paper sx={{ p: 3, mt: 2 }} variant="outlined">
          <Grid container spacing={3}>
            {/* Role Filter */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">User Roles</FormLabel>
                <FormGroup>
                  {(['admin', 'editor', 'contributor', 'viewer'] as UserRole[]).map(role => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={value.roles?.includes(role) || false}
                          onChange={(e) => handleRoleChange(role, e.target.checked)}
                          disabled={disabled}
                        />
                      }
                      label={role.charAt(0).toUpperCase() + role.slice(1)}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Activity Status Filter */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Activity Status</FormLabel>
                <RadioGroup
                  value={value.activityStatus || 'any'}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleActivityStatusChange(val === 'any' ? undefined : val as any);
                  }}
                >
                  <FormControlLabel
                    value="any"
                    control={<Radio />}
                    label="Any (active or inactive)"
                    disabled={disabled}
                  />
                  <FormControlLabel
                    value="active"
                    control={<Radio />}
                    label="Active (logged in within 30 days)"
                    disabled={disabled}
                  />
                  <FormControlLabel
                    value="inactive"
                    control={<Radio />}
                    label="Inactive (not logged in for 30+ days)"
                    disabled={disabled}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Registration Date Range */}
            <Grid item xs={12}>
              <FormLabel component="legend">Registration Date Range</FormLabel>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="From Date"
                    type="date"
                    value={value.registrationDateRange?.start ? format(value.registrationDateRange.start, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleDateRangeChange('start', e.target.value ? new Date(e.target.value) : null)}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="To Date"
                    type="date"
                    value={value.registrationDateRange?.end ? format(value.registrationDateRange.end, 'yyyy-MM-dd') : ''}
                    onChange={(e) => handleDateRangeChange('end', e.target.value ? new Date(e.target.value) : null)}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Custom User IDs */}
      {selectionMode === 'custom' && (
        <Paper sx={{ p: 3, mt: 2 }} variant="outlined">
          <TextField
            fullWidth
            multiline
            rows={6}
            label="User IDs (comma or newline separated)"
            placeholder="Enter user IDs, one per line or comma-separated"
            value={value.customUserIds?.join('\n') || ''}
            onChange={(e) => handleCustomUserIdsChange(e.target.value)}
            disabled={disabled}
            helperText="Enter one user ID per line or separate with commas"
          />
        </Paper>
      )}

      {/* Recipient Count */}
      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Calculating recipients...
            </Typography>
          </Box>
        ) : (
          <Alert severity={recipientCount > 0 ? 'info' : 'warning'}>
            <Typography variant="body2">
              <strong>{recipientCount}</strong> recipient{recipientCount !== 1 ? 's' : ''} will receive this broadcast
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default UserSelector;
